import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Breadcrumb } from "~/components/Breadcrumps";
import { SelectInput } from "~/components/Controls/Select";
import { Pagination } from "~/components/Pagination";
import { Progress } from "~/components/Progress";
import { BreadcrumbsResourcePage } from "~/components/ResourcePage";
import { Table } from "~/components/Table";
import { MultiSelectSeller } from "~/modules/Clients/components/SelectSeller";
import { clientsLinks } from "~/modules/Clients";
import { getGranularityDateFormat } from "~/modules/Dashboard/lib/performance";
import { Granularity } from "~/modules/Dashboard/models/Performance";
import { renderDollars } from "~/lib/helpers";
import { controllableUseResource } from "~/lib/loader";
import { stringDateFormat } from "~/lib/date";
import { fetchFinanceSummary, IFinanceSummaryFilter } from "~/modules/Payments/api";
import { PaymentsTypeNavigation } from "~/modules/Payments/components/PaymentsTypeNavigation";
import { netConditions, formatNetLabel, formatIntervalCovered, parseNetCondition } from "~/modules/Payments/models/FinanceSummary";
import { paymentsLinks } from "~/modules/Payments/links";

import style from "./PrePaymentsView.module.scss";

export const PrePaymentsView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dailyDateFormat = getGranularityDateFormat(Granularity.DAILY);

  const queryParams = new URLSearchParams(location.search);

  const sellerIds = useMemo(
    () =>
      queryParams
        .get("sellerIds")
        ?.split(",")
        .map((id) => Number.parseInt(id, 10)),
    [queryParams.get("sellerIds")]
  ) as number[];

  const net = parseNetCondition(queryParams.get("net"));

  const setQueryParams = useCallback(
    (params: Record<string, string | null>) => {
      const newQueryParams = new URLSearchParams(location.search);
      for (const [key, value] of Object.entries(params)) {
        if (value === null || value === "") {
          newQueryParams.delete(key);
          continue;
        }
        newQueryParams.set(key, value);
      }
      navigate({ search: newQueryParams.toString() }, { replace: true });
    },
    [location.search]
  );

  const filter: IFinanceSummaryFilter = useMemo(() => {
    return {
      accountIds: sellerIds ?? [],
      net: net ?? undefined,
    };
  }, [sellerIds, net]);

  const [paginator, setPaginator] = useState<{ page: number; pageSize: number }>({ page: 0, pageSize: 25 });

  useEffect(() => {
    setPaginator((prev) => ({ ...prev, page: 0 }));
  }, [filter]);

  const result = controllableUseResource(
    () =>
      fetchFinanceSummary(filter, {
        pageSize: paginator.pageSize,
        pageNo: paginator.page,
      }),
    { items: [], count: 0, page: 0, pageSize: 0 }
  );

  useEffect(() => {
    result.fetch();
  }, [paginator]);

  return (
    <Breadcrumb
      breadcrumb={{
        to: paymentsLinks.prePayments(),
        title: "Scheduled",
      }}
    >
      <BreadcrumbsResourcePage title="Scheduled">
        <PaymentsTypeNavigation sellerId={Array.isArray(sellerIds) && sellerIds.length === 1 ? sellerIds[0] : undefined} />

        <div className="layout-content-box">
          <form className={style.filters}>
            <MultiSelectSeller
              value={sellerIds}
              onChange={(sellerIds) => setQueryParams({ sellerIds: sellerIds.length ? sellerIds.join(",") : null })}
              className={style.bySeller}
              placeholder="Filter by Seller"
              disabled={result.isPending}
              searchable
              combobox
              autoFocus
              clearable
            />
            <SelectInput
              items={netConditions}
              className={style.byNet}
              value={net}
              onChange={(net) => setQueryParams({ net: net ?? null })}
              clearable
              disabled={result.isPending}
              placeholder="Net"
            />
          </form>

          <Table
            header
            columns={[
              {
                header: "Seller",
                cell: (row) => (
                  <NavLink to={clientsLinks.seller(row.accountId)}>{row.sellerName || row.accountId}</NavLink>
                ),
              },
              {
                header: "Net",
                cell: (row) => formatNetLabel(row.net),
              },
              {
                header: "Payout date",
                cell: (row) => (row.payoutDate ? stringDateFormat(row.payoutDate, dailyDateFormat) : "—"),
              },
              {
                header: "Payment Interval",
                cell: (row) => formatIntervalCovered(row.intervalCovered, dailyDateFormat),
              },
              {
                header: "Scheduled amount, $",
                cell: (row) => (typeof row.scheduledAmount === "number" ? renderDollars(row.scheduledAmount) : "—"),
              },
            ]}
            records={result.resource.items}
            placeholder={result.isPending && <Progress />}
          />
          {result.resource.count > result.resource.pageSize && (
            <Pagination
              padding={4}
              offset={result.resource.pageSize * result.resource.page}
              limit={result.resource.pageSize}
              total={result.resource.count}
              onChange={(page, perPage) => {
                setPaginator({ page, pageSize: perPage });
              }}
            />
          )}
        </div>
      </BreadcrumbsResourcePage>
    </Breadcrumb>
  );
};
